import { BVEvidenceChip, BVEvidenceStrength, BVStudioDraft, BVStudioOutput, BVStudioStatus, BVTemplateType } from './types';

export const BV_TEMPLATE_OPTIONS: Array<{
  value: BVTemplateType;
  label: string;
  description: string;
}> = [
  {
    value: 'social_ad',
    label: 'Social Ad',
    description: 'Short-form paid social copy with a primary message, headline, and support line.',
  },
  {
    value: 'search_ad',
    label: 'Search Ad',
    description: 'Keyword-friendly headlines and descriptions grounded in review proof points.',
  },
  {
    value: 'pdp_snippet',
    label: 'PDP Snippet',
    description: 'Retail-ready product detail page copy that maps review themes to benefits.',
  },
  {
    value: 'email_snippet',
    label: 'Email Snippet',
    description: 'Subject, preview text, and body copy for lifecycle or promo sends.',
  },
  {
    value: 'retail_buyer_kit',
    label: 'Retail Buyer Kit',
    description: 'Sell-in copy for retailer conversations backed by shopper evidence.',
  },
  {
    value: 'comparative',
    label: 'Comparative',
    description: 'Qualified competitive messaging with evidence-backed contrasts.',
  },
];

export const BV_RETAILER_SCOPE_OPTIONS = [
  { value: 'all', label: 'All retailers' },
  { value: 'amazon', label: 'Amazon only' },
  { value: 'bestbuy', label: 'Best Buy only' },
];

export const BV_VARIANT_OPTIONS = [1, 2, 3, 4, 5, 6];

type BadgeVariant = 'brand' | 'danger' | 'info' | 'secondary' | 'success' | 'warning';

function asNullableString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

function asRawOptionalString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : String(value);
}

function asNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export function createEmptyStudioDraft(): BVStudioDraft {
  return {
    template_type: 'social_ad',
    brand: '',
    product_name: null,
    competitor_set_id: null,
    competitor_brands: [],
    retailer_scope: 'all',
    rating_min: 4,
    rating_max: null,
    date_from: null,
    date_to: null,
    brand_voice_id: null,
    num_variants: 3,
    title: null,
    temperature: 0.4,
  };
}

export function draftFromStudioOutput(output: BVStudioOutput): BVStudioDraft {
  const params = output.generation_params ?? {};

  return {
    template_type: output.template_type,
    brand: output.brand_filter,
    product_name: asNullableString(params.product_name),
    competitor_set_id: asNullableNumber(params.competitor_set_id),
    competitor_brands: output.competitor_brands ?? [],
    retailer_scope: output.retailer_scope || 'all',
    rating_min: asNullableNumber(params.rating_min),
    rating_max: asNullableNumber(params.rating_max),
    date_from: asNullableString(params.date_from),
    date_to: asNullableString(params.date_to),
    brand_voice_id: asNullableNumber(params.brand_voice_id),
    num_variants: asNullableNumber(params.num_variants) ?? Math.max(output.content_variants.length + 1, 1),
    title: output.title || null,
    temperature: asNullableNumber(params.temperature) ?? 0.4,
  };
}

export function normalizeStudioDraft(draft: BVStudioDraft): BVStudioDraft {
  return {
    ...draft,
    brand: draft.brand.trim(),
    product_name: asRawOptionalString(draft.product_name),
    competitor_brands: asStringArray(draft.competitor_brands).filter(brand => brand !== draft.brand),
    retailer_scope: draft.retailer_scope || 'all',
    rating_min: draft.rating_min,
    rating_max: draft.rating_max,
    date_from: asNullableString(draft.date_from),
    date_to: asNullableString(draft.date_to),
    title: asRawOptionalString(draft.title),
  };
}

export function normalizeStudioDraftForRequest(draft: BVStudioDraft): BVStudioDraft {
  return {
    ...normalizeStudioDraft(draft),
    product_name: asNullableString(draft.product_name),
    date_from: asNullableString(draft.date_from),
    date_to: asNullableString(draft.date_to),
    title: asNullableString(draft.title),
  };
}

export function applyEvidenceChipToDraft(draft: BVStudioDraft, chip: BVEvidenceChip): BVStudioDraft {
  const params = (chip.params ?? {}) as Partial<BVStudioDraft>;

  return normalizeStudioDraft({
    ...draft,
    ...params,
    competitor_brands: Array.isArray(params.competitor_brands)
      ? asStringArray(params.competitor_brands)
      : draft.competitor_brands,
  });
}

export function formatTemplateLabel(templateType: BVTemplateType): string {
  return BV_TEMPLATE_OPTIONS.find(option => option.value === templateType)?.label ?? templateType;
}

export function formatStatusLabel(status: BVStudioStatus): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

export function getStatusBadgeVariant(status: BVStudioStatus): BadgeVariant {
  switch (status) {
    case 'approved':
      return 'success';
    case 'in_review':
      return 'warning';
    case 'rejected':
      return 'danger';
    case 'draft':
    default:
      return 'secondary';
  }
}

export function getEvidenceStrengthBadgeVariant(strength: BVEvidenceStrength): BadgeVariant {
  switch (strength) {
    case 'strong':
      return 'success';
    case 'weak':
      return 'danger';
    case 'moderate':
    default:
      return 'info';
  }
}

export function formatEvidenceStrength(strength: BVEvidenceStrength): string {
  return strength.charAt(0).toUpperCase() + strength.slice(1);
}

export function formatDateLabel(value: string | null | undefined): string {
  if (!value) {
    return 'Not set';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString();
}
